import { CaseSensitive } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { Alert, Button, Modal, Spinner } from "@/components/common";
import { useFixNameCasing } from "@/hooks/useClients";

interface NameCasingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Administrators: one-off clean-up of names saved in capitals before casing was enforced
 * — only our client records (the RODO client base), not policyholders from commission files.
 * Shows the changes first, saves on demand.
 */
export function NameCasingModal({ isOpen, onClose }: NameCasingModalProps) {
  const preview = useFixNameCasing();
  const apply = useFixNameCasing();
  const previewMutate = preview.mutate;

  useEffect(() => {
    if (isOpen) {
      previewMutate(false);
    }
  }, [isOpen, previewMutate]);

  const data = preview.data;
  const total = data?.clientsChanged ?? 0;

  function handleApply() {
    apply.mutate(true, {
      onSuccess: (result) => {
        toast.success(
          `Poprawiono imiona i nazwiska ${result.clientsChanged} klientów.`,
        );
        onClose();
      },
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Popraw wielkość liter w imionach i nazwiskach"
      className="max-w-2xl"
    >
      <div className="space-y-4">
        {(preview.isError || apply.isError) && (
          <Alert variant="error">
            {((preview.error ?? apply.error) as { message?: string }).message ??
              "Nie udało się"}
          </Alert>
        )}

        {preview.isPending || !data ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : total === 0 ? (
          <Alert variant="success">
            Wszystkie imiona i nazwiska są już zapisane poprawnie.
          </Alert>
        ) : (
          <>
            <p className="text-sm text-sub">
              Do poprawy: <strong>{data.clientsChanged}</strong> klientów z
              naszej bazy RODO. Ubezpieczający z plików prowizyjnych nie są tu
              brani pod uwagę.
            </p>
            <div className="max-h-80 overflow-y-auto rounded border border-edge-soft">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-panel">
                  <tr className="border-b border-edge text-left text-xs text-dim">
                    <th className="px-3 py-2 font-medium">Było</th>
                    <th className="px-3 py-2 font-medium">Będzie</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge-soft">
                  {data.changes.map((c) => (
                    <tr key={c.id}>
                      <td className="px-3 py-1.5 text-sub">{c.before}</td>
                      <td className="px-3 py-1.5 font-medium text-main">
                        {c.after}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.changes.length < total && (
              <p className="text-xs text-faint">
                Pokazano pierwsze {data.changes.length} zmian z {total}.
              </p>
            )}
          </>
        )}

        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" type="button" onClick={onClose}>
            Zamknij
          </Button>
          <Button
            type="button"
            leftIcon={<CaseSensitive size={16} aria-hidden />}
            isLoading={apply.isPending}
            disabled={total === 0}
            onClick={handleApply}
          >
            Zapisz poprawki
          </Button>
        </div>
      </div>
    </Modal>
  );
}
